import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Loader2, Bell, Heart, Reply, BellOff } from "lucide-react";
import { api } from "@/lib/api";
import { Avatar } from "@/components/common/Avatar";
import { VerifiedCheck } from "@/components/common/TrustBadge";
import { timeAgo } from "@/lib/format";
import { cn } from "@/lib/utils";

// One activity item — a reaction on your message or a reply to you.
function ActivityRow({ item, onOpen }) {
  const actor = item.actor || {};
  const isReaction = item.type === "reaction";
  const verb = isReaction ? "reacted" : "replied";
  return (
    <button
      onClick={() => onOpen(item)}
      className={cn(
        "flex w-full items-start gap-3 rounded-2xl border p-4 text-left transition-colors",
        item.read ? "border-white/8 bg-bloq-s1 hover:bg-white/5" : "border-bloq-mint/25 bg-bloq-mint/[0.06] hover:bg-bloq-mint/10",
      )}
      data-testid="activity-row"
    >
      <div className="relative shrink-0">
        <Avatar user={actor} size="sm" />
        <span
          className={cn(
            "absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full border-2 border-bloq-bg",
            isReaction ? "bg-bloq-purple/90 text-white" : "bg-bloq-mint text-bloq-bg",
          )}
        >
          {isReaction ? <Heart className="h-2.5 w-2.5" /> : <Reply className="h-2.5 w-2.5" />}
        </span>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-sm font-semibold">{actor.display_name || "Someone"}</span>
          {actor.verified && <VerifiedCheck size={12} />}
          <span className="text-sm text-bloq-muted">
            {verb} {isReaction && item.emoji ? item.emoji : ""}
          </span>
          {!item.read && <span className="ml-1 h-2 w-2 shrink-0 rounded-full bg-bloq-mint" />}
        </div>
        {item.preview && (
          <p className="mt-0.5 truncate text-sm text-bloq-muted" data-testid="activity-preview">
            “{item.preview}”
          </p>
        )}
        <span className="mt-1 block text-[11px] text-bloq-muted/70">{timeAgo(item.created_at)}</span>
      </div>
    </button>
  );
}

export default function ActivityPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["activity"], queryFn: api.activity });

  const items = data?.items || [];
  const unread = data?.unread || 0;

  const readMut = useMutation({
    mutationFn: () => api.readActivity(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["activity"] }),
  });

  useEffect(() => {
    if (unread > 0) readMut.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unread]);

  const open = (item) => {
    // Prefer the backend route (/chat/{conv}?msg=…), else construct the web path.
    const route = typeof item.route === "string" ? item.route : "";
    const conv = item.conversation_id || (route.match(/\/chat\/([^?]+)/) || [])[1];
    if (conv) navigate(`/app/chats/${conv}`);
  };

  return (
    <div className="h-full overflow-y-auto bg-bloq-bg" data-testid="activity-page">
      <div className="mx-auto max-w-2xl px-5 py-6 sm:px-8">
        <div className="mb-5 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="rounded-full p-1 text-bloq-muted hover:text-white md:hidden" data-testid="activity-back-btn">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex-1">
            <h1 className="flex items-center gap-2 text-2xl font-bold">
              <Bell className="h-5 w-5 text-bloq-mint" /> Activity
            </h1>
            <p className="mt-0.5 text-sm text-bloq-muted">Replies to you & reactions on your messages</p>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-16 text-bloq-muted"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="rounded-3xl border border-white/8 bg-bloq-s1 px-6 py-16 text-center" data-testid="activity-empty">
            <BellOff className="mx-auto mb-3 h-10 w-10 text-bloq-muted/60" />
            <p className="text-sm text-bloq-muted">No activity yet — reactions and replies to your messages will show up here.</p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {items.map((it, i) => <ActivityRow key={it.id || i} item={it} onOpen={open} />)}
          </div>
        )}
      </div>
    </div>
  );
}
